import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Button } from "@loot-os/ui/components/button";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * Reset an end user's identity verification so they can go through Veriff again (boxed parity:
 * `adminResetAccountKyc`). Editors and admins only; shown only once there is something to reset.
 */
export function KycControl({ endUserId, status }: { endUserId: string; status: string | null }) {
  const { can } = useTenantRole();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const reset = useMutation(
    trpc.users.resetKyc.mutationOptions({
      onSuccess: () => {
        toast.success("Identity verification reset; the shopper can verify again.");
        void queryClient.invalidateQueries({ queryKey: trpc.users.pathKey() });
      },
    }),
  );
  if (!can("editor") || status === null) return null;

  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={<Button type="button" variant="outline" size="sm" disabled={reset.isPending} />}
      >
        Reset verification
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset identity verification?</AlertDialogTitle>
          <AlertDialogDescription>
            Every Veriff session for this shopper is forgotten and they can start over. A lock set
            by a declined verification stays in place; unlock the account separately if you mean to.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => reset.mutate({ endUserId })}>Reset</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
